import { createSelector } from "reselect";

import { EncounterCategory } from "../models/encounterServiceEntity";
import {
  findAllGroupingSources,
  findGroupingResult,
  findGroupingSource,
} from "../services/encounter/encounterMapping";

import type { EncounterEntity } from "../models";
import type { State } from "../reducers";

const ipGroupingType = (state: State) => state.encounter.inProgress.ipGroupingType;
const opGroupingType = (state: State) => state.encounter.inProgress.opGroupingType;
const encounterType = (state: State) => state.encounter.inProgress.type;
const facility = (state: State) => state.encounter.inProgress.facility;
const ValidationResult = (state: State) => state.encounter.inProgress.ValidationResult;
const GroupingResults = (state: State) => state.encounter.inProgress.GroupingResults;
const selIsInPatient = (state: State) =>
  state.encounter.inProgress.ValidationResult?.Category === EncounterCategory.Inpatient;
const encounter = (state: State) => state.encounter.inProgress;

export const getCurrentIPGroupSourceSelector = createSelector(
  [encounter, ipGroupingType, GroupingResults, ValidationResult, encounterType, facility],
  (_encounter, _ipGroupingType) => findGroupingSource(true, _encounter, _ipGroupingType),
);

export const getCurrentIPGroupSourceAndResultSelector = createSelector(
  [encounter, ipGroupingType, GroupingResults, ValidationResult, encounterType, facility],
  (_encounter, _ipGroupingType) => {
    const groupingSource = findGroupingSource(true, _encounter, _ipGroupingType) || {};
    const groupingResult = findGroupingResult(true, _encounter, _ipGroupingType);

    const combined = {
      ...groupingSource,
      Outdated: groupingResult?.Key !== _encounter.ValidationResult?.GroupingResultsKey,
      Values: groupingResult?.Values ? { ...groupingResult?.Values } : {},
    };

    return combined;
  },
);

export const getCurrentOPGroupSourceSelector = createSelector(
  [encounter, opGroupingType, GroupingResults, ValidationResult, encounterType, facility],
  (_encounter, _opGroupingType) => findGroupingSource(false, _encounter, _opGroupingType),
);

export const getCurrentOPGroupSourceAndResultSelector = createSelector(
  [encounter, opGroupingType, GroupingResults, ValidationResult, encounterType, facility],
  (_encounter, _opGroupingType) => {
    const groupingSource = findGroupingSource(false, _encounter, _opGroupingType) || {};
    const groupingResult = findGroupingResult(false, _encounter, _opGroupingType);

    const combined = {
      ...groupingSource,
      Outdated: groupingResult?.Key !== _encounter.ValidationResult?.GroupingResultsKey,
      Values: groupingResult?.Values ? { ...groupingResult?.Values } : {},
    };

    return combined;
  },
);

export const getCurrentIPGrouperTypeForResult = (
  IsInpatient: boolean,
  _inProgress: EncounterEntity,
  _ipGroupingType?: string,
) => {
  if (!IsInpatient) {
    return "";
  }

  const ipGrouping = findGroupingSource(true, _inProgress, _ipGroupingType);
  if (!ipGrouping) {
    return "";
  }

  return ipGrouping.GrouperType;
};

export const getCurrentOPGrouperTypeForResult = (
  IsInpatient: boolean,
  _encounter: EncounterEntity,
  _opGroupingType?: string,
) => {
  if (IsInpatient) {
    return "";
  }

  const opGrouping = findGroupingSource(false, _encounter, _opGroupingType);
  if (!opGrouping) {
    return "";
  }

  return opGrouping.GrouperType;
};

export const getCurrentOPGrouperType = createSelector(
  [selIsInPatient, encounter, opGroupingType, encounterType, facility],
  (_selIsInPatient, _encounter, _opGroupingType) =>
    getCurrentOPGrouperTypeForResult(_selIsInPatient, _encounter, _opGroupingType),
);

export const getIPGroupingOptsSelector = createSelector(
  [encounter, ValidationResult, GroupingResults, encounterType, facility],
  (_encounter) => ({
    dataSource: findAllGroupingSources(true, _encounter),
    dataTextField: "title",
    dataValueField: "id",
  }),
);

export const getOPGroupingOptsSelector = createSelector(
  [encounter, ValidationResult, GroupingResults, encounterType, facility],
  (_encounter) => ({
    dataSource: findAllGroupingSources(false, _encounter),
    dataTextField: "title",
    dataValueField: "id",
  }),
);
